import { CanvasAiPatchError } from "./canvasAiPatch";
import { OpenRouterApiError } from "./openRouter.api";

const ERROR_MESSAGES_BY_STATUS = Object.freeze({
  401: "This OpenRouter key is invalid or has been revoked.",
  402: "This OpenRouter account does not have enough credits.",
  403: "This key or model is not permitted by the OpenRouter account.",
  408: "OpenRouter timed out. Please try again.",
  429: "This model is currently rate-limited and may be unreliable. Try another model.",
  502: "This model's provider returned an invalid response. Try another model.",
  503: "This model's provider is temporarily unavailable. Try another model.",
});

const getErrorChain = (error) => {
  const errors = [];
  let currentError = error;
  while (currentError && errors.length < 4) {
    errors.push(currentError);
    currentError = currentError.cause ?? currentError.lastError;
  }
  return errors;
};

const readStatus = (error) => {
  const status = getErrorChain(error).find(
    (candidate) => candidate?.status ?? candidate?.statusCode ?? candidate?.response?.status
  );
  return Number(status?.status ?? status?.statusCode ?? status?.response?.status) || 0;
};

const isNamedError = ({ error, name }) =>
  getErrorChain(error).some((candidate) => candidate?.name === name);

const readErrorText = (error) =>
  getErrorChain(error).map((candidate) => candidate?.message).filter(Boolean).join(" ");

/** @param {unknown} error @returns {string} */
export const getCanvasAiErrorMessage = (error) => {
  if (isNamedError({ error, name: "AbortError" })) {
    return "Generation stopped. Nothing was applied.";
  }
  if (error?.name === "CanvasAiConflictError") return error.message;
  const statusMessage = ERROR_MESSAGES_BY_STATUS[readStatus(error)];
  if (statusMessage) return statusMessage;
  if (error instanceof CanvasAiPatchError) return error.message;
  if (error instanceof OpenRouterApiError) return error.message;
  const errorText = readErrorText(error);
  if (/rate[- ]limit|too many requests|retry shortly/i.test(errorText)) {
    return "This model is currently rate-limited and may be unreliable. Try another model.";
  }
  if (/context|token limit/i.test(errorText)) {
    return "The conversation is too large for this model. Start a new chat.";
  }
  if (isNamedError({ error, name: "AI_NoOutputGeneratedError" })) {
    return "This model returned no usable response and may be unreliable. Try another model.";
  }

  return "The AI request failed. Please try again.";
};
